import { CheckboxItem } from 'components/CopyIteration/CheckboxItem';
import HighlightedTable from 'components/HighlightedTable/HighlightedTable';
import { DateTime } from 'luxon';
import { RoutingPlans } from 'schemas/RoutingPlan.schema';
import { capitaliseFirstChar, conditionTargetToLabel, getMessagePlanStatusColour } from 'utils/formattingUtils';
import './TeamRoutingPlans.css';
import { Tag } from 'nhsuk-react-components';

interface TeamRoutingPlansProps {
  routingPlans: RoutingPlans;
  selectedRoutingPlans?: RoutingPlans;
  onSelectPlan?: (id: string, version: number | undefined) => void;
  onRemovePlan?: (id: string, version: number | undefined) => void;
}

function TeamRoutingPlans({
  routingPlans,
  selectedRoutingPlans,
  onSelectPlan,
  onRemovePlan,
}: TeamRoutingPlansProps) {
  if (routingPlans.length === 0) {
    return null;
  }

  return (
    <HighlightedTable>
      <HighlightedTable.HeaderRow>
        {onSelectPlan && (
          <HighlightedTable.HeaderValue className="team-routing-plans-table__header-chosen">
            Selected (
            {selectedRoutingPlans?.length || 0}
            )
          </HighlightedTable.HeaderValue>
        )}
        <HighlightedTable.HeaderValue className="team-routing-plans-table__header-name">
          Name
        </HighlightedTable.HeaderValue>
        <HighlightedTable.HeaderValue className="team-routing-plans-table__header-last-updated">
          Last updated
        </HighlightedTable.HeaderValue>
        <HighlightedTable.HeaderValue className="team-routing-plans-table__header-status">
          Status
        </HighlightedTable.HeaderValue>
        {onRemovePlan && !selectedRoutingPlans && (
          <HighlightedTable.HeaderValue className="team-routing-plans-table__header-action">
            Action
          </HighlightedTable.HeaderValue>
        )}
      </HighlightedTable.HeaderRow>
      {routingPlans
        .toSorted((a, b) => a.routing_plan_name.localeCompare(b.routing_plan_name))
        .map((plan) => {
          const dt = DateTime.fromMillis(Number(plan.created_at));

          return (
            <HighlightedTable.Row
              key={plan.routing_plan_id + plan.version}
              className={
                plan.status === 'draft'
                  ? 'team-routing-plans-table__row--draft'
                  : undefined
              }
            >
              {onSelectPlan && (
                <HighlightedTable.Value>
                  <div>
                    <CheckboxItem
                      label={`Select ${plan.routing_plan_name} routing plan`}
                      isLabelVisible={false}
                      id="1"
                      checked={
                        selectedRoutingPlans?.some(
                          (selectedPlan) => `${selectedPlan.routing_plan_id}:${selectedPlan.version}`
                            === `${plan.routing_plan_id}:${plan.version}`,
                        ) ?? false
                      }
                      onChange={(event) => {
                        if (event.currentTarget.checked) {
                          onSelectPlan?.(plan.routing_plan_id, plan.version);
                          return;
                        }

                        onRemovePlan?.(plan.routing_plan_id, plan.version);
                      }}
                    />
                  </div>
                </HighlightedTable.Value>
              )}
              <HighlightedTable.Value>
                <div>
                  <div className="nhsuk-u-font-weight-bold">
                    {plan.routing_plan_name}
                  </div>
                  {plan.comms_target && (
                    <div className="nhsuk-u-margin-top-2 nhsuk-u-secondary-text-colour">
                      Target:
                      {conditionTargetToLabel(plan.comms_target)}
                    </div>
                  )}
                </div>
              </HighlightedTable.Value>
              <HighlightedTable.Value>
                {plan.created_at && (
                  <>
                    <div>{dt.toFormat('dd LLL yyyy')}</div>
                    <div>{dt.toFormat('HH:mm')}</div>
                  </>
                )}
              </HighlightedTable.Value>
              <HighlightedTable.Value>
                <Tag modifier={getMessagePlanStatusColour(plan.status)}>{capitaliseFirstChar(plan.status)}</Tag>
              </HighlightedTable.Value>
              {onRemovePlan && !selectedRoutingPlans && (
                <HighlightedTable.Value>
                  <button
                    type="button"
                    className="nhsuk-back-link__link team-routing-plans-table__remove-button"
                    onClick={() => onRemovePlan(plan.routing_plan_id, plan.version)}
                  >
                    Remove
                  </button>
                </HighlightedTable.Value>
              )}
            </HighlightedTable.Row>
          );
        })}
    </HighlightedTable>
  );
}

export default TeamRoutingPlans;
